import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Popper from '@material-ui/core/Popper';
import Paper from '@material-ui/core/Paper';
import Grow from '@material-ui/core/Grow';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ReplayIcon from '@material-ui/icons/Replay';
import Typography from '@material-ui/core/Typography';
import FilterIcon from '../../assets/icons/FilterIcon.svg';
import IconOrder from '../../assets/icons/IconOrder.svg';
import { getCollectionsService } from '../../services/collections';
import { KEY_SELECTED_CATEGORY } from '../../constants.js/keys';
import { extractCollectionRows, getChildCollections } from '../../utils/collections-tree';


const useStyles = makeStyles({
    btn: {
        "& .MuiButton-label": {
            fontSize: 14
        },
        "& .MuiButton-contained": {
            background: '#fff'
        }
    }
});



const FilterProductsSelect = ({
    storeId,
    selectedCategorie,
    setSelectedCategorie,
    selectedSubcategory,
    setSelectedSubcategory,
    selectedStatus,
    setSelectedStatus,
    setSelectedOrder,
    selectedOrder,
    onClearFilters,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [openCategorie, setOpenCategorie] = useState(false);
    const [openSubcategorie, setOpenSubcategorie] = useState(false);
    const [openOrder, setOpenOrder] = useState(false);
    const [collectionData, setCollectionData] = useState([]);
    const [allCollectionsRows, setAllCollectionsRows] = useState([]);
    const [subcategoryData, setSubcategoryData] = useState([]);
    const [categorySel, setCategorySel] = useState(JSON.parse(localStorage.getItem(KEY_SELECTED_CATEGORY)));

    const anchorRef = useRef(null);
    const anchorRefCategorie = useRef(null);
    const anchorRefSubcategorie = useRef(null);
    const anchorRefOrder = useRef(null);

    const statusOptions = [t('Product.filter.inactive'), t('Product.filter.active')];
    const orderOptions = [
        { value: null, label: t('Product.filter.orderDefault') },
        { value: 'asc', label: t('Product.filter.orderAz') },
        { value: 'desc', label: t('Product.filter.orderDz') },
    ];

    const getOrderLabel = () => {
        if (selectedOrder === 'asc') return t('Product.filter.orderAz');
        if (selectedOrder === 'desc') return t('Product.filter.orderDz');
        return t('Product.filter.orderDefault');
    };

    const hasActiveFilters =
        selectedStatus !== null ||
        selectedCategorie ||
        selectedSubcategory ||
        selectedOrder;

    const handleClick = () => {
        setOpen(open ? false : true);
    };

    const handleClickCategorie = () => {
        setOpenCategorie(openCategorie ? false : true);
    };

    const handleClickOrder = () => {
        setOpenOrder(openOrder ? false : true);
    };
    const handleClickSubCategorie = () => {
        setOpenSubcategorie(openSubcategorie ? false : true);
    };



    useEffect(() => {
        getCollectionsService(storeId)
            .then(({ data }) => {
                const rows = extractCollectionRows(data);
                setAllCollectionsRows(rows);
                setCollectionData(rows.filter((r) => !r.parent_id).sort((a, b) => Number(b.default) - Number(a.default)));
            })
            .catch(err => {
                setCollectionData([]);
                setAllCollectionsRows([]);
            });
    }, [storeId]);

    useEffect(() => {
        if (categorySel) {
           setSelectedCategorie(categorySel) 
        }
     }, [categorySel, selectedCategorie, setSelectedCategorie]);

    useEffect(() => {
        if (!selectedCategorie || !selectedCategorie.id) {
            setSubcategoryData([]);
            setSelectedSubcategory(null);
            return;
        }

        const directSubs = getChildCollections(selectedCategorie);
        const fallbackSubs = allCollectionsRows.filter(
            (row) => String(row.parent_id) === String(selectedCategorie.id)
        );
        const merged = [...(Array.isArray(directSubs) ? directSubs : []), ...fallbackSubs];
        const unique = merged.filter(
            (sub, index, arr) => arr.findIndex((x) => String(x.id) === String(sub.id)) === index
        );
        const sorted = unique.sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
        setSubcategoryData(sorted);
        if (
            selectedSubcategory &&
            !sorted.some((sub) => String(sub.id) === String(selectedSubcategory.id))
        ) {
            setSelectedSubcategory(null);
        }
    }, [selectedCategorie, allCollectionsRows, selectedSubcategory, setSelectedSubcategory]);

    const handleClickClearFilter = () => {
        setSelectedStatus(null);
        setSelectedCategorie('');
        setSelectedSubcategory(null);
        setSelectedOrder(null);
        setCategorySel(null);
        localStorage.removeItem(KEY_SELECTED_CATEGORY);
        if (onClearFilters) {
            onClearFilters();
        }
    };

    const handleMenuItemClickStatus = (index) => {
        setSelectedStatus(index);
        setOpen(false);
    };

    const handleMenuItemClickCategorie = (option) => {
        setSelectedCategorie(option);
        setCategorySel(option)
        setOpenCategorie(false);
        localStorage.setItem(KEY_SELECTED_CATEGORY, JSON.stringify(option))
    };
    const handleMenuItemClickSubCategorie = (option) => {
        setSelectedSubcategory(option);
        setOpenSubcategorie(false);
    };


    const handleMenuItemClickSortName = (orderValue) => {
        setSelectedOrder(orderValue);
        setOpenOrder(false);
    };

    const handleCloseCategorie = (event) => {
        if (anchorRefCategorie.current && anchorRefCategorie.current.contains(event.target)) {
            return;
        }
        setOpenCategorie(false);
    };

    const handleCloseOrder = (event) => {
        if (anchorRefOrder.current && anchorRefOrder.current.contains(event.target)) {
            return;
        }
        setOpenOrder(false);
    };

    const handleCloseStatus = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };
    const handleCloseSubCategorie = (event) => {
        if (anchorRefSubcategorie.current && anchorRefSubcategorie.current.contains(event.target)) {
            return;
        }
        setOpenSubcategorie(false);
    };

    
    return (
        <Grid container>
            <Grid item container xs={8} style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Typography
                    variant="body2"
                    style={{ margin: 8 }}
                >
                    <img style={{ marginRight: 5 }} src={FilterIcon} alt="" />
                    {t('Product.filter.by')}
                </Typography>
                <ButtonGroup className={classes.btn} variant="contained" disableElevation={true}>
                    <Button
                        onClick={handleClickCategorie}
                        endIcon={<ExpandMoreIcon />}
                        ref={anchorRefCategorie}
                        aria-controls={openCategorie ? 'split-button-categorie' : undefined}
                        aria-expanded={openCategorie ? 'true' : undefined}
                    >
                        {selectedCategorie ? selectedCategorie.name : t('Product.filter.categorie')}
                    </Button>
                    <Button
                        onClick={handleClick}
                        endIcon={<ExpandMoreIcon />}
                        ref={anchorRef}
                        aria-controls={open ? 'split-button-menu' : undefined}
                        aria-expanded={open ? 'true' : undefined}
                        aria-label="select merge strategy"
                        aria-haspopup="menu" >
                        {selectedStatus !== null ? statusOptions[selectedStatus] : t('Product.filter.visibility')}
                    </Button>
                    <Button
                        onClick={handleClickSubCategorie}
                        endIcon={<ExpandMoreIcon />}
                        ref={anchorRefSubcategorie}
                        aria-controls={openSubcategorie ? 'split-button-subcategorie' : undefined}
                        aria-expanded={openSubcategorie ? 'true' : undefined}
                        disabled={!selectedCategorie || subcategoryData.length === 0}
                    >
                        {selectedSubcategory ? selectedSubcategory.name : t('Product.filter.subcategory')}
                    </Button>

                    <Button
                        onClick={handleClickClearFilter}
                        disabled={!hasActiveFilters}
                        startIcon={<ReplayIcon />}>{t('Product.filter.empty')}</Button>
                </ButtonGroup>


                <Popper
                    open={openCategorie}
                    anchorEl={anchorRefCategorie.current}
                    role={undefined}
                    transition
                    placement="bottom-start"
                    style={{ zIndex: 9999 }}
                    modifiers={{
                        offset: { enabled: true, offset: '4, 4' },
                    }}
                >
                    {({ TransitionProps, placement }) => (
                        <Grow
                            {...TransitionProps}
                            style={{
                                transformOrigin: placement === 'bottom' ? 'left top' : 'left bottom',
                            }}
                        >
                            <Paper>
                                <ClickAwayListener onClickAway={handleCloseCategorie}>
                                    <MenuList id="split-button-categorie">
                                        {collectionData.map((option, index) => (
                                            <MenuItem
                                                key={option.id}
                                                onClick={(event) => handleMenuItemClickCategorie(option)}
                                            >
                                                {option.name}
                                            </MenuItem>
                                        ))}
                                    </MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>

                <Popper
                    open={openSubcategorie}
                    anchorEl={anchorRefSubcategorie.current}
                    role={undefined}
                    transition
                    placement="bottom-start"
                    style={{ zIndex: 9999 }}
                    modifiers={{
                        offset: { enabled: true, offset: '4, 4' },
                    }}
                >
                    {({ TransitionProps, placement }) => (
                        <Grow
                            {...TransitionProps}
                            style={{
                                transformOrigin: placement === 'bottom' ? 'left top' : 'left bottom',
                            }}
                        >
                            <Paper>
                                <ClickAwayListener onClickAway={handleCloseSubCategorie}>
                                    <MenuList id="split-button-subcategorie">
                                        {subcategoryData.map((option) => (
                                            <MenuItem
                                                key={option.id}
                                                selected={
                                                    selectedSubcategory &&
                                                    String(option.id) === String(selectedSubcategory.id)
                                                }
                                                onClick={() => handleMenuItemClickSubCategorie(option)}
                                            >
                                                {option.name}
                                            </MenuItem>
                                        ))}
                                    </MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>

                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    role={undefined}
                    transition
                    placement="bottom-start"
                    style={{ zIndex: 9999 }}
                    modifiers={{
                        offset: { enabled: true, offset: '4, 4' },
                    }}
                >
                    {({ TransitionProps, placement }) => (
                        <Grow
                            {...TransitionProps}
                            style={{
                                transformOrigin: placement === 'bottom' ? 'left top' : 'left bottom',
                            }}
                        >
                            <Paper>
                                <ClickAwayListener onClickAway={handleCloseStatus}>
                                    <MenuList id="split-button-menu">
                                        {statusOptions.map((option, index) => (
                                            <MenuItem
                                                key={option}
                                                selected={index === selectedStatus}
                                                onClick={(event) => handleMenuItemClickStatus(index)}
                                            >
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>


            </Grid>
            <Grid item container xs={4} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Typography
                    variant="body2"
                    style={{ margin: 8 }}>
                    <img style={{ marginRight: 5 }} src={IconOrder} />
                    {t('Product.filter.order')}
                </Typography>
                <ButtonGroup className={classes.btn} variant="contained" ref={anchorRefOrder} disableElevation={true}>
                    <Button
                        onClick={handleClickOrder}
                        endIcon={<ExpandMoreIcon />}>{getOrderLabel()}</Button>
                </ButtonGroup>


                <Popper
                    open={openOrder}
                    anchorEl={anchorRefOrder.current}
                    role={undefined}
                    transition
                    placement="bottom-end"
                    style={{ zIndex: 9999 }}
                    modifiers={{
                        offset: { enabled: true, offset: '4, 4' },
                    }}
                >
                    {({ TransitionProps, placement }) => (
                        <Grow
                            {...TransitionProps}
                            style={{
                                transformOrigin: placement === 'bottom' ? 'right top' : 'right bottom',
                            }}
                        >
                            <Paper>
                                <ClickAwayListener onClickAway={handleCloseOrder}>
                                    <MenuList id="split-button-order">
                                        {orderOptions.map((option) => (
                                            <MenuItem
                                                key={option.label}
                                                selected={selectedOrder === option.value}
                                                onClick={() => handleMenuItemClickSortName(option.value)}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>

            </Grid>
        </Grid>
    );
}

const mapStateToProps = ({ auth }) => ({
    storeId: auth.storeId
});


export default connect(
    mapStateToProps
)(FilterProductsSelect);